import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useParams, useNavigate } from 'react-router-dom';
import { GlassButton } from '../ui';
import {
  Eye, Download, Globe, Plus, Loader2, Wand2,
  Monitor, Tablet, Smartphone, ExternalLink, Copy, Check, Trash2,
  MoreHorizontal, Home, LayoutGrid, Palette, Calculator, MapPin, Upload,
} from 'lucide-react';
import axios from 'axios';
import './SiteBuilder.css';

import { getBrandGuides, BrandGuide, previewPublishMigrationCheck, MigrationPreviewSummary, importStaticSite } from '../../services/api';
import PrePublishMigrationModal from '../SEO/PrePublishMigrationModal';
import { SiteProject, SiteDocument, PageDocument, SectionInstance } from './types';
import ComponentBrowser from './ComponentBrowser';
import PricingPageWizard from './PricingPageWizard';
import LocationPagesPanel from './LocationPagesPanel';
import SectionWrapper from './SectionWrapper';
import PropertiesPanel from './PropertiesPanel';
import SEOPanel from './SEOPanel';
import NetlifyConnectPanel from './NetlifyConnectPanel';
import AimeHostedConnectPanel from './AimeHostedConnectPanel';
import SiteCustomCodePanel from './SiteCustomCodePanel';
import SitePreview from './SitePreview';
import { SectionRenderer } from './renderers';
import FloatingToolbar from './FloatingToolbar';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  // If REACT_APP_API_URL is a relative path like "/api", resolve against current origin
  if (envUrl && envUrl.startsWith('/')) {
    return `${window.location.origin}${envUrl}`;
  }
  return envUrl || `${window.location.protocol}//${window.location.hostname}:8001/api`;
};

// ── Nav out-of-sync detection ────────────────────────────────────────────────
function isNavOutOfSync(siteDoc: SiteDocument): boolean {
  if (!siteDoc?.pages?.length) return false;

  // Build the expected set of internal page hrefs
  const pageHrefs = new Set(siteDoc.pages.map(p => '/' + p.slug));

  // Find any nav section across all pages
  let navSection: SectionInstance | undefined;
  outer: for (const page of siteDoc.pages) {
    for (const section of page.sections) {
      if (section.component_id === 'nav-standard' || section.component_id === 'nav-mega') {
        navSection = section;
        break outer;
      }
    }
  }

  // No nav found — nothing to sync
  if (!navSection) return false;

  const links = navSection.controls?.links;

  // Nav has no links at all and there are 2+ pages → out of sync
  if (!Array.isArray(links) || links.length === 0) {
    return pageHrefs.size >= 2;
  }

  // Extract internal hrefs: starts with '/', no '//', not just '/'
  const navInternalHrefs = new Set(
    links
      .filter((l: any) =>
        typeof l?.href === 'string' &&
        l.href.startsWith('/') &&
        !l.href.includes('//') &&
        l.href !== '/'
      )
      .map((l: any) => l.href as string)
  );

  // Compare sets
  if (navInternalHrefs.size !== pageHrefs.size) return true;
  for (const href of Array.from(pageHrefs)) {
    if (!navInternalHrefs.has(href)) return true;
  }
  return false;
}

/** Extract the first font-family name from a CSS font-family string. */
function extractFontFamily(fontStr: string | undefined): string | null {
  if (!fontStr) return null;
  // Handles "'Permanent Marker', cursive", "Inter", etc.
  const m = fontStr.match(/['"]?([^'",]+)['"]?/);
  const name = m ? m[1].trim() : null;
  if (!name || ['Inter', '-apple-system', 'sans-serif', 'system-ui', 'serif', 'monospace', 'cursive', 'fantasy'].includes(name)) return null;
  return name;
}

/** Inject (or update) a Google Fonts <link> into document <head> based on brand fonts. */
function syncGoogleFonts(brand: Record<string, any> | null | undefined): void {
  const LINK_ID = 'sb-canvas-google-fonts';
  const existing = document.getElementById(LINK_ID);

  if (!brand) {
    existing?.remove();
    return;
  }

  const headingFam = extractFontFamily(brand.heading_font);
  const bodyFam = extractFontFamily(brand.body_font);
  const families = Array.from(new Set([headingFam, bodyFam].filter(Boolean) as string[]));

  if (families.length === 0) {
    existing?.remove();
    return;
  }

  const fontsParam = families.map(f => `${f.replace(/ /g, '+')}:wght@400;500;600;700;800`).join('&family=');
  const href = `https://fonts.googleapis.com/css2?family=${fontsParam}&display=swap`;

  if (existing) {
    (existing as HTMLLinkElement).href = href;
    return;
  }
  const link = document.createElement('link');
  link.id = LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/** Build React CSSProperties with brand CSS custom properties AND real inherited properties. */
function buildBrandCssVars(brand: Record<string, any> | null | undefined): React.CSSProperties {
  if (!brand) return {};
  const varMap: Record<string, string> = {
    primary_color:            '--brand-primary',
    secondary_color:          '--brand-secondary',
    accent_color:             '--brand-accent',
    text_color:               '--brand-text',
    text_light:               '--brand-text-light',
    background:               '--brand-bg',
    background_alt:           '--brand-bg-alt',
    heading_font:             '--brand-heading-font',
    body_font:                '--brand-body-font',
    button_radius:            '--brand-btn-radius',
    card_radius:              '--brand-card-radius',
    heading_weight:           '--brand-heading-weight',
    heading_letter_spacing:   '--brand-heading-letter-spacing',
    heading_line_height:      '--brand-heading-line-height',
    hero_font_size:           '--brand-hero-font-size',
  };
  const vars: Record<string, string> = {};
  for (const [key, cssVar] of Object.entries(varMap)) {
    if (brand[key]) vars[cssVar] = brand[key];
  }
  // Also apply as real inheritable CSS properties so React tsx renderers
  // and their inline-styled children pick them up without explicit var() references.
  if (brand.body_font)  vars['fontFamily'] = brand.body_font;
  if (brand.text_color) vars['color']      = brand.text_color;
  return vars as React.CSSProperties;
}

const SiteBuilder: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteProject | null>(null);
  const [siteDoc, setSiteDoc] = useState<SiteDocument | null>(null);
  const [activePageId, setActivePageId] = useState<string>('');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canvasDevice, setCanvasDevice] = useState<'desktop' | 'tablet' | 'phone'>('desktop');
  const [publishBanner, setPublishBanner] = useState<{ url: string; visible: boolean } | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [navOutOfSync, setNavOutOfSync] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Brand guide picker
  const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
  const [brandGuideUpdating, setBrandGuideUpdating] = useState(false);

  // Add page modal state
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [addPageError, setAddPageError] = useState('');
  const [addingPage, setAddingPage] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const addPageTitleRef = useRef<HTMLInputElement>(null);

  // Pricing page wizard
  const [showPricingWizard, setShowPricingWizard] = useState(false);
  const [showLocationPanel, setShowLocationPanel] = useState(false);

  // Pre-publish migration check state
  const [migrationModalOpen, setMigrationModalOpen] = useState(false);
  const [migrationSummary, setMigrationSummary] = useState<MigrationPreviewSummary | null>(null);
  const [migrationLoading, setMigrationLoading] = useState(false);

  // Page tab context menu state
  const [pageMenuOpenId, setPageMenuOpenId] = useState<string | null>(null);
  const [renamingPageId, setRenamingPageId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const pageMenuRef = useRef<HTMLDivElement | null>(null);

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Load site + inject Google Fonts from brand
  useEffect(() => {
    if (!siteId) { setLoading(false); return; }
    axios.get(`${getApiBase()}/site-builder/sites/${siteId}`, { headers })
      .then(res => {
        setSite(res.data);
        syncGoogleFonts(res.data?.site_meta?.brand);
        const doc = res.data.site_document as SiteDocument;
        setSiteDoc(doc);
        if (doc?.pages?.length) setActivePageId(doc.pages[0].page_id);
      })
      .catch(() => navigate('/site-builder'))
      .finally(() => setLoading(false));
    // Remove fonts link when leaving the site builder
    return () => { document.getElementById('sb-canvas-google-fonts')?.remove(); };
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load brand guides list for the top-bar picker
  useEffect(() => {
    getBrandGuides().then(data => setBrandGuides(data || [])).catch(() => {});
  }, []);

  const handleBrandGuideChange = async (newBrandGuideId: string) => {
    if (!siteId) return;
    setBrandGuideUpdating(true);
    try {
      await axios.put(
        `${getApiBase()}/site-builder/sites/${siteId}`,
        { brand_guide_id: newBrandGuideId || '' },
        { headers },
      );
      // Re-fetch via GET so site_meta.brand (enriched brand context) is populated
      const full = await axios.get(`${getApiBase()}/site-builder/sites/${siteId}`, { headers });
      setSite(full.data);
      syncGoogleFonts(full.data?.site_meta?.brand);
    } catch { /* ignore */ } finally {
      setBrandGuideUpdating(false);
    }
  };

  // Auto-derive slug from title unless user has manually edited the slug
  useEffect(() => {
    if (!slugManuallyEdited) {
      setNewPageSlug(
        newPageTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      );
    }
  }, [newPageTitle, slugManuallyEdited]);

  // Auto-focus title input when modal opens
  useEffect(() => {
    if (showAddPageModal) {
      setTimeout(() => addPageTitleRef.current?.focus(), 50);
    }
  }, [showAddPageModal]);

  // Close page context menu on outside click
  useEffect(() => {
    if (!pageMenuOpenId) return;
    const handler = (e: MouseEvent) => {
      if (pageMenuRef.current && !pageMenuRef.current.contains(e.target as Node)) {
        setPageMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pageMenuOpenId]);

  // Update nav-out-of-sync flag whenever siteDoc changes
  useEffect(() => {
    if (siteDoc) setNavOutOfSync(isNavOutOfSync(siteDoc));
  }, [siteDoc]);

  const activePage = siteDoc?.pages?.find(p => p.page_id === activePageId) || null;
  const selectedSection = activePage?.sections.find(s => s.section_id === selectedSectionId) || null;

  // Auto-save with debounce
  const saveDoc = useCallback((doc: SiteDocument) => {
    if (!siteId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${getApiBase()}/site-builder/sites/${siteId}`, {
          site_document: doc,
        }, { headers });
      } catch (e) {
        console.error('Auto-save failed', e);
      } finally {
        setSaving(false);
      }
    }, 1500);
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDoc = useCallback((updater: (doc: SiteDocument) => SiteDocument) => {
    setSiteDoc(prev => {
      if (!prev) return prev;
      const next = updater(prev);
      saveDoc(next);
      return next;
    });
  }, [saveDoc]);

  // Section operations
  const handleControlChange = useCallback((sectionId: string, key: string, value: any) => {
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p => ({
        ...p,
        sections: p.sections.map(s =>
          s.section_id === sectionId ? { ...s, controls: { ...s.controls, [key]: value } } : s
        ),
      })),
    }));
  }, [updateDoc]);

  const handleDeleteSection = useCallback((sectionId: string) => {
    if (selectedSectionId === sectionId) setSelectedSectionId(null);
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p => ({
        ...p,
        sections: p.sections.filter(s => s.section_id !== sectionId),
      })),
    }));
  }, [updateDoc, selectedSectionId]);

  const handleDuplicateSection = useCallback((sectionId: string) => {
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p => {
        const idx = p.sections.findIndex(s => s.section_id === sectionId);
        if (idx === -1) return p;
        const dup: SectionInstance = {
          ...JSON.parse(JSON.stringify(p.sections[idx])),
          section_id: crypto.randomUUID(),
        };
        const sections = [...p.sections];
        sections.splice(idx + 1, 0, dup);
        return { ...p, sections };
      }),
    }));
  }, [updateDoc]);

  const handleMoveSection = useCallback((sectionId: string, direction: 'up' | 'down') => {
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p => {
        const idx = p.sections.findIndex(s => s.section_id === sectionId);
        if (idx === -1) return p;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= p.sections.length) return p;
        const sections = [...p.sections];
        [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
        return { ...p, sections };
      }),
    }));
  }, [updateDoc]);

  const handleSectionDragEnd = useCallback((result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p => {
        if (!activePage || p.page_id !== activePage.page_id) return p;
        const sections = [...p.sections];
        const [removed] = sections.splice(result.source.index, 1);
        sections.splice(result.destination!.index, 0, removed);
        return { ...p, sections };
      }),
    }));
  }, [updateDoc, activePage]);

  const handleAddComponent = useCallback((componentId: string, variant?: string, defaults?: Record<string, any>) => {
    if (!activePageId) return;
    const newSection: SectionInstance = {
      section_id: crypto.randomUUID(),
      component_id: componentId,
      variant: variant || '',
      controls: { ...(defaults || {}) },
      locked: false,
    };
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p =>
        p.page_id === activePageId
          ? { ...p, sections: [...p.sections, newSection] }
          : p
      ),
    }));
    setSelectedSectionId(newSection.section_id);
  }, [activePageId, updateDoc]);

  /** Insert multiple sections (e.g., from PricingPageWizard) into the active page. */
  const handleInsertSections = useCallback((sections: SectionInstance[]) => {
    if (!activePageId || sections.length === 0) return;
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p =>
        p.page_id === activePageId
          ? { ...p, sections: [...p.sections, ...sections] }
          : p
      ),
    }));
    setShowPricingWizard(false);
  }, [activePageId, updateDoc]);

  /** Re-fetch site after LocationPagesPanel commits new area pages. */
  const handleLocationCommitted = useCallback(async () => {
    if (!siteId) return;
    try {
      const siteRes = await axios.get(`${getApiBase()}/site-builder/sites/${siteId}`, { headers });
      setSite(siteRes.data);
      setSiteDoc(siteRes.data.site_document);
    } catch (e) {
      console.error('Failed to reload site after location pages commit', e);
    }
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegenerateSection = useCallback(async (sectionId: string) => {
    if (!siteId || !activePage) return;
    const section = activePage.sections.find(s => s.section_id === sectionId);
    if (!section) return;
    try {
      const res = await axios.post(`${getApiBase()}/site-builder/generate-section`, {
        site_project_id: siteId,
        page_id: activePageId,
        component_id: section.component_id,
        variant: section.variant,
      }, { headers, timeout: 60000 });
      updateDoc(doc => ({
        ...doc,
        pages: doc.pages.map(p => ({
          ...p,
          sections: p.sections.map(s =>
            s.section_id === sectionId ? { ...s, controls: res.data.controls || {} } : s
          ),
        })),
      }));
    } catch (e) {
      console.error('Section regeneration failed', e);
    }
  }, [siteId, activePageId, activePage, updateDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update a single SEO field on the active page
  const handlePageSEOChange = useCallback((field: keyof PageDocument, value: string | boolean) => {
    if (!activePageId) return;
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(p =>
        p.page_id === activePageId ? { ...p, [field]: value } : p
      ),
    }));
  }, [activePageId, updateDoc]);

  const handleOpenAddPageModal = useCallback(() => {
    setNewPageTitle('');
    setNewPageSlug('');
    setAddPageError('');
    setSlugManuallyEdited(false);
    setShowAddPageModal(true);
  }, []);

  const handleCloseAddPageModal = useCallback(() => {
    if (addingPage) return;
    setShowAddPageModal(false);
  }, [addingPage]);

  const handleConfirmAddPage = useCallback(async () => {
    if (!siteId) return;
    const title = newPageTitle.trim();
    const slug = newPageSlug.trim();
    if (!title) { setAddPageError('Page title is required.'); return; }
    if (!slug) { setAddPageError('Slug is required.'); return; }
    setAddPageError('');
    setAddingPage(true);
    try {
      const res = await axios.post(`${getApiBase()}/site-builder/sites/${siteId}/pages`, {
        title, slug, sections: [],
      }, { headers });
      const siteRes = await axios.get(`${getApiBase()}/site-builder/sites/${siteId}`, { headers });
      setSite(siteRes.data);
      setSiteDoc(siteRes.data.site_document);
      setActivePageId(res.data.page_id);
      setShowAddPageModal(false);
    } catch (e: any) {
      const msg = getApiError(e, 'Failed to add page. Please try again.');
      setAddPageError(msg);
    } finally {
      setAddingPage(false);
    }
  }, [siteId, newPageTitle, newPageSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRenamePage = useCallback(async (pageId: string) => {
    if (!siteId) return;
    const title = renameValue.trim();
    if (!title) { setRenamingPageId(null); return; }
    const slug = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      await axios.put(`${getApiBase()}/site-builder/sites/${siteId}/pages/${pageId}`, { title, slug }, { headers });
      const siteRes = await axios.get(`${getApiBase()}/site-builder/sites/${siteId}`, { headers });
      setSite(siteRes.data);
      setSiteDoc(siteRes.data.site_document);
    } catch (e) {
      console.error('Rename page failed', e);
    } finally {
      setRenamingPageId(null);
    }
  }, [siteId, renameValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeletePage = useCallback(async (pageId: string) => {
    if (!siteId || !siteDoc) return;
    if (siteDoc.pages.length < 2) return;
    try {
      await axios.delete(`${getApiBase()}/site-builder/sites/${siteId}/pages/${pageId}`, { headers });
      const siteRes = await axios.get(`${getApiBase()}/site-builder/sites/${siteId}`, { headers });
      setSite(siteRes.data);
      const newDoc = siteRes.data.site_document as SiteDocument;
      setSiteDoc(newDoc);
      if (activePageId === pageId && newDoc?.pages?.length) {
        setActivePageId(newDoc.pages[0].page_id);
      }
    } catch (e) {
      console.error('Delete page failed', e);
    } finally {
      setPageMenuOpenId(null);
    }
  }, [siteId, siteDoc, activePageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMovePage = useCallback((pageId: string, direction: 'left' | 'right') => {
    if (!siteDoc) return;
    const idx = siteDoc.pages.findIndex(p => p.page_id === pageId);
    if (idx === -1) return;
    const newIdx = direction === 'left' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= siteDoc.pages.length) return;
    updateDoc(doc => {
      const pages = [...doc.pages];
      [pages[idx], pages[newIdx]] = [pages[newIdx], pages[idx]];
      return { ...doc, pages };
    });
    setPageMenuOpenId(null);
  }, [siteDoc, updateDoc]);

  const handleSyncNav = useCallback(() => {
    if (!siteDoc) return;
    const pageLinks = siteDoc.pages.map(p => ({ label: p.title, href: '/' + p.slug }));
    updateDoc(doc => ({
      ...doc,
      pages: doc.pages.map(page => ({
        ...page,
        sections: page.sections.map(section => {
          if (section.component_id !== 'nav-standard' && section.component_id !== 'nav-mega') {
            return section;
          }
          const existingLinks: Array<{ label: string; href: string }> =
            Array.isArray(section.controls?.links) ? section.controls.links : [];
          const externalLinks = existingLinks.filter(
            (l: any) =>
              typeof l?.href === 'string' &&
              (!l.href.startsWith('/') || l.href.includes('//'))
          );
          return {
            ...section,
            controls: { ...section.controls, links: [...pageLinks, ...externalLinks] },
          };
        }),
      })),
    }));
    setNavOutOfSync(false);
  }, [siteDoc, updateDoc]);

  // The actual publish — called after migration check passes or user proceeds anyway
  const doPublish = async () => {
    if (!siteId) return;
    setMigrationModalOpen(false);
    try {
      await axios.post(`${getApiBase()}/site-builder/sites/${siteId}/publish`, {}, { headers });
      const now = new Date().toISOString();
      setSite(prev => prev ? { ...prev, status: 'published', published_at: now } : prev);
      const previewUrl = `${window.location.origin}/api/site-builder/sites/${siteId}/preview`;
      setPublishBanner({ url: previewUrl, visible: true });
      setCopiedUrl(false);
    } catch (e) {
      console.error('Publish failed', e);
    }
  };

  const handlePublish = async () => {
    if (!siteId) return;

    // IMPR-004: if site is still null or has no project_id, skip preview (fail-open)
    if (!site?.project_id) {
      return doPublish();
    }

    // BLOCKER-001: page slugs are host-less paths ("/about") — the backend normalizer
    // keys on "host/path" so path-only strings would never match historical GSC keys
    // (e.g. "example.com/about"), causing every GSC-tracked page to false-alarm as a
    // URL change on every publish of an unchanged site.  Send [] instead and let the
    // backend fall back to the latest audit live-set (full URLs), exactly like
    // WebsitesList does.  Candidate URLs are only useful here if we can resolve the
    // site's public host — that is a fast-follow; for now the audit live-set is the
    // most accurate available source.
    const candidateUrls: string[] = [];

    setMigrationLoading(true);
    setMigrationSummary(null);
    try {
      const summary = await previewPublishMigrationCheck({
        project_id: site.project_id,
        candidate_urls: candidateUrls,
      });
      setMigrationSummary(summary);
      setMigrationLoading(false);
      if (summary.has_risk) {
        setMigrationModalOpen(true);
        // publish deferred to onProceed
        return;
      }
      // No risk — publish directly
      return doPublish();
    } catch {
      // Fail-open: preview errored → publish without warning
      setMigrationLoading(false);
      return doPublish();
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(() => {
      // Fallback for environments without clipboard API
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const getPreviewUrl = () =>
    `${window.location.origin}/api/site-builder/sites/${siteId}/preview`;

  const handleExport = async () => {
    if (!siteId) return;
    try {
      const res = await axios.post(`${getApiBase()}/site-builder/sites/${siteId}/export-html`, {}, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${site?.name || 'site'}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Loader2 size={32} className="spin" color="#2563eb" />
      </div>
    );
  }

  if (!siteId) {
    // Site list view
    return <SiteList />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
        borderBottom: '1px solid rgba(0,0,0,0.08)', background: 'var(--panel, #fff)',
        flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem', marginRight: 8 }}>
          {site?.name || 'Site Builder'}
        </span>

        {/* Page tabs */}
        <div style={{ display: 'flex', gap: 4, flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {siteDoc?.pages?.map((p, idx) => {
            const isActive = p.page_id === activePageId;
            const isFirst = idx === 0;
            const isLast = idx === (siteDoc.pages.length - 1);
            const isRenaming = renamingPageId === p.page_id;
            const isMenuOpen = pageMenuOpenId === p.page_id;
            return (
              <div key={p.page_id} className="sb-page-tab-wrap" ref={isMenuOpen ? pageMenuRef : undefined}>
                {isRenaming ? (
                  <input
                    className="sb-page-rename-input"
                    value={renameValue}
                    autoFocus
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRenamePage(p.page_id);
                      if (e.key === 'Escape') setRenamingPageId(null);
                    }}
                    onBlur={() => handleRenamePage(p.page_id)}
                  />
                ) : (
                  <button
                    onClick={() => { setActivePageId(p.page_id); setSelectedSectionId(null); }}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: '0.8125rem', fontWeight: 500,
                      background: isActive ? 'var(--brand-primary, #2563eb)' : 'transparent',
                      color: isActive ? '#fff' : 'var(--text-secondary, #64748b)',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    {isFirst && <Home size={10} style={{ flexShrink: 0 }} />}
                    {p.title}
                  </button>
                )}
                {!isRenaming && (
                  <button
                    className="sb-page-menu-btn"
                    onClick={e => {
                      e.stopPropagation();
                      setPageMenuOpenId(isMenuOpen ? null : p.page_id);
                    }}
                    title="Page options"
                  >
                    <MoreHorizontal size={13} />
                  </button>
                )}
                {isMenuOpen && (
                  <div className="sb-page-dropdown">
                    <button
                      className="sb-page-dropdown-item"
                      onMouseDown={e => {
                        e.preventDefault();
                        setRenameValue(p.title);
                        setRenamingPageId(p.page_id);
                        setPageMenuOpenId(null);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      className={`sb-page-dropdown-item${isFirst ? ' disabled' : ''}`}
                      onMouseDown={e => { e.preventDefault(); if (!isFirst) handleMovePage(p.page_id, 'left'); }}
                      disabled={isFirst}
                    >
                      Move Left
                    </button>
                    <button
                      className={`sb-page-dropdown-item${isLast ? ' disabled' : ''}`}
                      onMouseDown={e => { e.preventDefault(); if (!isLast) handleMovePage(p.page_id, 'right'); }}
                      disabled={isLast}
                    >
                      Move Right
                    </button>
                    {(siteDoc?.pages?.length ?? 0) > 1 && (
                      <button
                        className="sb-page-dropdown-item danger"
                        onMouseDown={e => { e.preventDefault(); handleDeletePage(p.page_id); }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={handleOpenAddPageModal} style={{
            padding: '4px 8px', borderRadius: 6, border: '1px dashed var(--stroke, #cbd5e1)',
            background: 'transparent', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-secondary, #94a3b8)',
            display: 'inline-flex', alignItems: 'center',
          }}>
            <Plus size={14} />
          </button>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Brand guide selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Brand guide">
            <Palette size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
            <select
              value={site?.brand_guide_id || ''}
              onChange={e => handleBrandGuideChange(e.target.value)}
              disabled={brandGuideUpdating}
              style={{
                fontSize: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 6,
                padding: '4px 6px', background: '#fff', color: '#475569', cursor: 'pointer',
                maxWidth: 140,
              }}
            >
              <option value="">No brand guide</option>
              {brandGuides.map(b => (
                <option key={b.brand_guide_id} value={b.brand_guide_id}>
                  {b.brand_name || b.brand_guide_name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.1)' }} />
          {saving && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Saving...</span>}
          {site?.status === 'published' && (
            <span className="sb-published-badge">
              <Globe size={12} />
              Published
            </span>
          )}
          <GlassButton variant="ghost" onClick={() => setShowPreview(true)} style={{ padding: '6px 12px' }}>
            <Eye size={16} style={{ marginRight: 4 }} /> Preview
          </GlassButton>
          {site?.status === 'published' && (
            <GlassButton
              variant="secondary"
              onClick={() => window.open(getPreviewUrl(), '_blank')}
              style={{ padding: '6px 12px' }}
            >
              <ExternalLink size={16} style={{ marginRight: 4 }} /> View Live
            </GlassButton>
          )}
          <GlassButton variant="ghost" onClick={handleExport} style={{ padding: '6px 12px' }}>
            <Download size={16} style={{ marginRight: 4 }} /> Export
          </GlassButton>
          <GlassButton onClick={handlePublish} style={{ padding: '6px 16px' }}>
            <Globe size={16} style={{ marginRight: 4 }} />
            {site?.status === 'published' ? 'Republish' : 'Publish'}
          </GlassButton>
        </div>
      </div>

      {/* Publish success banner */}
      {publishBanner?.visible && (
        <div className="sb-publish-banner">
          <Globe size={16} className="sb-publish-banner-icon" />
          <span className="sb-publish-banner-label">Published successfully!</span>
          <a
            href={publishBanner.url}
            target="_blank"
            rel="noopener noreferrer"
            className="sb-publish-banner-url"
          >
            {publishBanner.url}
          </a>
          <button
            className="sb-publish-banner-copy"
            onClick={() => handleCopyUrl(publishBanner.url)}
            title="Copy URL"
          >
            {copiedUrl ? <Check size={14} /> : <Copy size={14} />}
            {copiedUrl ? 'Copied!' : 'Copy URL'}
          </button>
          <button
            className="sb-publish-banner-close"
            onClick={() => setPublishBanner(null)}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Nav out-of-sync banner */}
      {navOutOfSync && (
        <div className="sb-nav-sync-banner" style={{ flexShrink: 0 }}>
          <LayoutGrid size={15} style={{ flexShrink: 0 }} />
          <span>Your navigation links may be out of date.</span>
          <button className="sb-nav-sync-banner-btn" onClick={handleSyncNav}>
            Update Navigation →
          </button>
          <button
            className="sb-nav-sync-banner-dismiss"
            onClick={() => setNavOutOfSync(false)}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Main 3-panel layout */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: Component browser */}
        <ComponentBrowser onAddComponent={handleAddComponent} />

        {/* Center: Device toolbar + Canvas */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Device toggle toolbar */}
          <div className="sb-device-toolbar">
            <button
              className={`sb-device-btn${canvasDevice === 'desktop' ? ' active' : ''}`}
              onClick={() => setCanvasDevice('desktop')}
              title="Desktop"
            >
              <Monitor size={15} /> Desktop
            </button>
            <button
              className={`sb-device-btn${canvasDevice === 'tablet' ? ' active' : ''}`}
              onClick={() => setCanvasDevice('tablet')}
              title="Tablet (768px)"
            >
              <Tablet size={15} /> Tablet
            </button>
            <button
              className={`sb-device-btn${canvasDevice === 'phone' ? ' active' : ''}`}
              onClick={() => setCanvasDevice('phone')}
              title="Phone (375px)"
            >
              <Smartphone size={15} /> Phone
            </button>
            {canvasDevice !== 'desktop' && (
              <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                {canvasDevice === 'tablet' ? '768px' : '375px'}
              </span>
            )}
            {/* Pricing page generator shortcut */}
            <button
              onClick={() => setShowPricingWizard(true)}
              title="Generate a 'how much does {service} cost in {area}?' page"
              style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                background: 'rgba(33,119,255,0.08)', border: '1px solid rgba(33,119,255,0.2)',
                borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 600, color: '#2177FF',
              }}
            >
              <Calculator size={14} /> Add Pricing Page
            </button>
            {/* Areas We Serve location pages shortcut */}
            <button
              onClick={() => setShowLocationPanel(true)}
              title="Generate unique landing pages for each service area (anti-doorway, SEO-optimised)"
              style={{
                marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 5,
                background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
                borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 600, color: '#059669',
              }}
            >
              <MapPin size={14} /> Areas We Serve
            </button>
          </div>

          {/* Canvas scroll area */}
          <div
            style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', padding: canvasDevice === 'desktop' ? 0 : '16px' }}
            onClick={() => setSelectedSectionId(null)}
          >
            <div
              className={`sb-canvas-frame${canvasDevice !== 'desktop' ? ` ${canvasDevice}` : ''}`}
              style={{ maxWidth: canvasDevice === 'desktop' ? 1200 : undefined }}
            >
              {/* Scoped CSS reset + brand vars — mirrors SITE_BASE_CSS baseline so React renderers match the Python preview */}
              <style>{`
                .sb-canvas-root *, .sb-canvas-root *::before, .sb-canvas-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
                .sb-canvas-root img { max-width: 100%; display: block; }
                .sb-canvas-root a { color: inherit; }
                .sb-canvas-root h1, .sb-canvas-root h2, .sb-canvas-root h3,
                .sb-canvas-root h4, .sb-canvas-root h5, .sb-canvas-root h6 {
                  font-family: var(--brand-heading-font, inherit);
                  font-weight: var(--brand-heading-weight, 700);
                  letter-spacing: var(--brand-heading-letter-spacing, normal);
                  line-height: var(--brand-heading-line-height, 1.1);
                }
              `}</style>
              <div className="sb-canvas-root" style={{ background: '#fff', boxShadow: canvasDevice === 'desktop' ? '0 0 40px rgba(0,0,0,0.08)' : 'none', minHeight: '100%', ...buildBrandCssVars(site?.site_meta?.brand) }}>
                <DragDropContext onDragEnd={handleSectionDragEnd}>
                  <Droppable droppableId="sb-canvas">
                    {(droppableProvided) => (
                      <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                        {activePage?.sections.map((section, idx) => (
                          <Draggable key={section.section_id} draggableId={section.section_id} index={idx}>
                            {(draggableProvided) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                              >
                                <SectionWrapper
                                  section={section}
                                  index={idx}
                                  total={activePage.sections.length}
                                  isSelected={selectedSectionId === section.section_id}
                                  onSelect={() => setSelectedSectionId(section.section_id)}
                                  onMoveUp={() => handleMoveSection(section.section_id, 'up')}
                                  onMoveDown={() => handleMoveSection(section.section_id, 'down')}
                                  onDuplicate={() => handleDuplicateSection(section.section_id)}
                                  onDelete={() => handleDeleteSection(section.section_id)}
                                  onRegenerate={() => handleRegenerateSection(section.section_id)}
                                  onControlChange={handleControlChange}
                                  dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                                  projectId={site?.project_id}
                                >
                                  <SectionRenderer section={section} />
                                </SectionWrapper>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                {(!activePage || activePage.sections.length === 0) && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    height: 300, color: '#94a3b8',
                  }}>
                    <Plus size={32} style={{ marginBottom: 12 }} />
                    <p>Add components from the left panel</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Properties panel + SEO panel stacked in a shared scrollable column */}
        <div style={{
          width: 280, flexShrink: 0,
          borderLeft: '1px solid rgba(0,0,0,0.08)',
          display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto',
        }}>
          <PropertiesPanel
            section={selectedSection}
            siteProjectId={siteId || ''}
            onControlChange={handleControlChange}
            onDeleteSection={handleDeleteSection}
            onRegenerateSection={handleRegenerateSection}
            brandLogos={(() => {
              const bg = brandGuides.find(b => b.brand_guide_id === site?.brand_guide_id);
              if (!bg) return [];
              return [
                { label: 'Primary Logo',    url: bg.logo_url },
                { label: 'Dark BG Logo',    url: bg.logo_dark_bg_url },
                { label: 'Mono (Black)',    url: bg.logo_monochrome_black_url },
                { label: 'Mono (White)',    url: bg.logo_monochrome_white_url },
                { label: 'Favicon / Icon', url: bg.favicon_url },
              ].filter(l => !!l.url) as { label: string; url: string }[];
            })()}
            embedded
          />
          <SEOPanel
            page={activePage}
            onPageSEOChange={handlePageSEOChange}
          />
          {siteId && <SiteCustomCodePanel siteId={siteId} />}
          {siteId && (
            <>
              <div style={{ paddingTop: 4, paddingBottom: 2 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Publishing &amp; Custom Domain
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
                  Make your site available to visitors. AIME-hosted is instant with no deploy step; Netlify lets you own your hosting infrastructure.
                </div>
              </div>
              <AimeHostedConnectPanel
                siteId={siteId}
                siteName={site?.name || 'this site'}
              />
              <NetlifyConnectPanel
                siteId={siteId}
                siteName={site?.name || 'this site'}
              />
            </>
          )}
        </div>
      </div>

      {/* Preview overlay */}
      {showPreview && siteDoc && (
        <SitePreview
          siteDoc={siteDoc}
          activePage={activePage}
          brandCssVars={buildBrandCssVars(site?.site_meta?.brand)}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Floating text-formatting toolbar — appears on text selection inside inline-edit elements */}
      <FloatingToolbar />

      {/* Add Page modal */}
      {showAddPageModal && (
        <div
          className="sb-add-page-modal-overlay"
          onMouseDown={e => { if (e.target === e.currentTarget) handleCloseAddPageModal(); }}
          onKeyDown={e => { if (e.key === 'Escape') handleCloseAddPageModal(); }}
          tabIndex={-1}
        >
          <div className="sb-add-page-modal" role="dialog" aria-modal="true">
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
              Add Page
            </h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Title <span style={{ color: 'var(--danger, #dc2626)' }}>*</span>
              </label>
              <input
                ref={addPageTitleRef}
                className="sb-modal-input"
                type="text"
                value={newPageTitle}
                placeholder="e.g. About Us"
                onChange={e => setNewPageTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleConfirmAddPage(); if (e.key === 'Escape') handleCloseAddPageModal(); }}
                disabled={addingPage}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Slug
              </label>
              <input
                className="sb-modal-input"
                type="text"
                value={newPageSlug}
                placeholder="e.g. about-us"
                onChange={e => { setSlugManuallyEdited(true); setNewPageSlug(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter') handleConfirmAddPage(); if (e.key === 'Escape') handleCloseAddPageModal(); }}
                disabled={addingPage}
              />
            </div>
            {addPageError && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--danger, #dc2626)', margin: '0 0 12px' }}>
                {addPageError}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <GlassButton variant="secondary" onClick={handleCloseAddPageModal} disabled={addingPage} style={{ padding: '6px 14px' }}>
                Cancel
              </GlassButton>
              <GlassButton onClick={handleConfirmAddPage} disabled={addingPage} style={{ padding: '6px 14px' }}>
                {addingPage ? <Loader2 size={14} className="spin" style={{ marginRight: 6 }} /> : null}
                Add Page
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Page Wizard modal */}
      {showPricingWizard && site && (
        <PricingPageWizard
          projectId={site.project_id}
          brandGuideId={site.brand_guide_id}
          siteId={siteId}
          onInsert={handleInsertSections}
          onClose={() => setShowPricingWizard(false)}
        />
      )}

      {/* Areas We Serve — Location Pages Panel */}
      {showLocationPanel && siteId && (
        <LocationPagesPanel
          siteId={siteId}
          onCommitted={handleLocationCommitted}
          onClose={() => setShowLocationPanel(false)}
        />
      )}

      {/* Pre-publish migration warning modal */}
      <PrePublishMigrationModal
        isOpen={migrationModalOpen}
        summary={migrationSummary}
        loading={migrationLoading}
        onProceed={doPublish}
        onCancel={() => setMigrationModalOpen(false)}
      />
    </div>
  );
};


// Site list sub-component
const SiteList: React.FC = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState<Array<any>>([]);
  const [projects, setProjects] = useState<Array<{ project_id: string; name: string }>>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  // Per-card delete state: null = no confirm, 'confirming' = showing confirm UI, 'deleting' = in-flight, 'error' = failed
  const [deleteState, setDeleteState] = useState<Record<string, 'confirming' | 'deleting' | 'error'>>({});

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };
  const apiBase = getApiBase();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importPct, setImportPct] = useState(0);

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = '';
    if (!file || !selectedProject) return;
    setImporting(true);
    setImportPct(0);
    try {
      const name = file.name.replace(/\.zip$/i, '') || 'Imported Site';
      const created = await importStaticSite(selectedProject, name, file, setImportPct);
      navigate(`/site-builder/imported/${created.site_project_id}`);
    } catch (err: any) {
      const msg = err?.code === 'ECONNABORTED'
        ? 'Import timed out while uploading. The bundle is large or the connection is slow — try a smaller zip or a faster network.'
        : (err?.response?.data?.detail || err.message);
      alert('Import failed: ' + msg);
    } finally {
      setImporting(false);
      setImportPct(0);
    }
  };

  useEffect(() => {
    axios.get(`${apiBase}/projects/`, { headers }).then(res => {
      setProjects(res.data || []);
      if (res.data?.length) setSelectedProject(res.data[0].project_id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProject) return;
    axios.get(`${apiBase}/site-builder/sites?project_id=${selectedProject}`, { headers })
      .then(res => setSites(res.data || []))
      .catch(() => setSites([]));
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteClick = (e: React.MouseEvent, siteId: string) => {
    e.stopPropagation();
    setDeleteState(prev => ({ ...prev, [siteId]: 'confirming' }));
  };

  const handleDeleteCancel = (e: React.MouseEvent, siteId: string) => {
    e.stopPropagation();
    setDeleteState(prev => {
      const next = { ...prev };
      delete next[siteId];
      return next;
    });
  };

  const handleDeleteConfirm = async (e: React.MouseEvent, siteId: string) => {
    e.stopPropagation();
    setDeleteState(prev => ({ ...prev, [siteId]: 'deleting' }));
    try {
      await axios.delete(`${apiBase}/site-builder/sites/${siteId}`, { headers });
      setSites(prev => prev.filter(s => s.site_project_id !== siteId));
      setDeleteState(prev => {
        const next = { ...prev };
        delete next[siteId];
        return next;
      });
    } catch {
      setDeleteState(prev => ({ ...prev, [siteId]: 'error' }));
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', margin: 0 }}>Site Builder</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.875rem' }}>
            Component-based website generation and editing
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} style={{
            padding: '8px 12px', border: '1px solid var(--stroke)', borderRadius: 8,
            background: 'var(--panel)', color: 'var(--text)', fontSize: '0.875rem',
          }}>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <input
            ref={importInputRef}
            type="file"
            accept=".zip"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
          <GlassButton
            variant="secondary"
            onClick={() => importInputRef.current?.click()}
            disabled={importing || !selectedProject}
          >
            <Upload size={16} style={{ marginRight: 6 }} /> {importing ? (importPct < 100 ? `Uploading… ${importPct}%` : 'Processing…') : 'Import Site'}
          </GlassButton>
          <GlassButton onClick={() => navigate('/site-builder/new')}>
            <Wand2 size={16} style={{ marginRight: 6 }} /> Generate New Site
          </GlassButton>
        </div>
      </div>

      {sites.length === 0 && !loading ? (
        <div style={{
          textAlign: 'center', padding: 80, color: 'var(--text-secondary)',
        }}>
          <Globe size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
          <p style={{ fontSize: '1.125rem', marginBottom: 8 }}>No sites yet</p>
          <p>Generate your first site with AI or create one manually.</p>
          <GlassButton onClick={() => navigate('/site-builder/new')} style={{ marginTop: 16 }}>
            <Wand2 size={16} style={{ marginRight: 6 }} /> Generate New Site
          </GlassButton>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {sites.map(s => {
            const cardDeleteState = deleteState[s.site_project_id];
            const isConfirming = cardDeleteState === 'confirming';
            const isDeleting = cardDeleteState === 'deleting';
            const isError = cardDeleteState === 'error';

            return (
              <div
                key={s.site_project_id}
                onClick={() => !isConfirming && !isDeleting && navigate(
                  s.source_type === 'imported_static'
                    ? `/site-builder/imported/${s.site_project_id}`
                    : `/site-builder/${s.site_project_id}`
                )}
                className={`sb-site-card${s.status === 'published' ? ' published' : ''}${isConfirming || isDeleting ? ' sb-site-card--confirming' : ''}`}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
              >
                {/* Delete button — top-right, fades in on card hover */}
                {!isConfirming && !isDeleting && (
                  <button
                    className="sb-site-card-delete-btn"
                    onClick={e => handleDeleteClick(e, s.site_project_id)}
                    title="Delete site"
                  >
                    <Trash2 size={15} />
                  </button>
                )}

                {/* Status badge row */}
                <div className="sb-site-card-status-row">
                  <span className={`sb-site-card-badge${s.status === 'published' ? ' published' : ''}`}>
                    {s.status === 'published' ? (
                      <><Globe size={11} /> Published</>
                    ) : (
                      'Draft'
                    )}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {s.page_count} {s.page_count === 1 ? 'page' : 'pages'}
                  </span>
                </div>

                <h3 style={{ fontSize: '1.125rem', margin: '8px 0 4px' }}>
                  {s.favicon_url && (
                    <img src={s.favicon_url} alt="" width={16} height={16} className="sb-site-card-favicon"
                         onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                  )}
                  {s.name}
                </h3>

                {/* Published date */}
                {s.status === 'published' && s.updated_at && (
                  <p className="sb-site-card-published-date">
                    Published {new Date(s.updated_at).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </p>
                )}

                {s.description && (
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
                    {s.description.slice(0, 100)}{s.description.length > 100 ? '...' : ''}
                  </p>
                )}

                {/* Inline delete confirmation */}
                {(isConfirming || isDeleting || isError) && (
                  <div
                    className="sb-site-card-confirm"
                    onClick={e => e.stopPropagation()}
                  >
                    {isError ? (
                      <>
                        <p className="sb-site-card-confirm-msg" style={{ color: 'var(--danger)' }}>
                          Delete failed — please try again
                        </p>
                        <div className="sb-site-card-confirm-actions">
                          <button
                            className="sb-site-card-confirm-cancel"
                            onClick={e => handleDeleteCancel(e, s.site_project_id)}
                          >
                            Dismiss
                          </button>
                          <button
                            className="sb-site-card-confirm-delete"
                            onClick={e => handleDeleteConfirm(e, s.site_project_id)}
                          >
                            Retry
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="sb-site-card-confirm-msg">
                          {s.status === 'published'
                            ? 'This site is published and will be taken offline. Delete anyway?'
                            : 'Delete this site?'}
                        </p>
                        <div className="sb-site-card-confirm-actions">
                          <button
                            className="sb-site-card-confirm-cancel"
                            onClick={e => handleDeleteCancel(e, s.site_project_id)}
                            disabled={isDeleting}
                          >
                            Cancel
                          </button>
                          <button
                            className="sb-site-card-confirm-delete"
                            onClick={e => handleDeleteConfirm(e, s.site_project_id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <Loader2 size={13} className="spin" style={{ marginRight: 4 }} />
                            ) : null}
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SiteBuilder;
